import {accountingTypes} from 'helpers/constants'
import {get, isNil} from 'lodash'
import {formatRoundedNumber} from "helpers/financial"

export const getDefaultTaxCode = (taxCodes) => {
  return taxCodes.find(taxCode => taxCode.isDefault)
}

export const getTaxCodeNameFor = (taxCodeId, taxCodes) => {
  const taxCode = taxCodes.find(taxCode => taxCode.id === taxCodeId)
  return getTaxCodeName(taxCode)
}

export const getTaxCodeName = (taxCode) => {
  const code = get(taxCode, 'code', null)
  const rate = get(taxCode, 'rate', 0)
  return isNil(code)
      ? null
      : `${code} (${getTaxRateDisplay(rate)}%)`
}

export const getTaxCodeOption = (taxCode) => {
  const taxCodeName = getTaxCodeName(taxCode)
  return {
    id: taxCode.id,
    value: taxCode.id,
    key: taxCode.id,
    label: taxCodeName,
    name: taxCodeName,
    taxCode,
  }
}

export const mapTaxCodesToOptions = (taxCodes) => {
  return taxCodes.map(taxCode => getTaxCodeOption(taxCode))
}

export const getAccountingTaxCodeOption = (accountingTaxCode) => {
  const {code, name, rate} = accountingTaxCode
  const label = isNil(name) ? code : name + ''

  return {
    key: code,
    value: code,
    label: `${label} (${rate}%)`,
    rate,
  }
}

export const getAccountingTaxCodeOptionWithPrefix = (accountingTaxCode) => {
  const {code, name, rate, isPurchasing} = accountingTaxCode
  const label = isNil(name) ? code : name + ''

  return {
    key: code,
    value: code,
    label: `(${isPurchasing ? 'Expense' : 'Revenue'}) ${label} (${rate}%)`,
    rate,
  }
}

export const findAccountingTaxCode = (accountingTaxCodes, code) => {
  return accountingTaxCodes.find(accountingTaxCode => accountingTaxCode.code === code)
}

export const getAccountingTaxCodes = (accountingTaxCodes, accountsIntegration) => {
  const salesTaxCodes = filterToSalesAccountingTaxCodes(accountingTaxCodes, accountsIntegration)
      .map(taxCode => {
        return {
          ...taxCode,
          isPurchasing: false,
        }
      })

  const purchaseTaxCodes = filterToPurchaseAccountingTaxCodes(accountingTaxCodes, accountsIntegration)
      .map(taxCode => {
        return {
          ...taxCode,
          isPurchasing: true,
        }
      })

  return [
    ...salesTaxCodes,
    ...purchaseTaxCodes,
  ].map(getAccountingTaxCodeOptionWithPrefix)
}

export const getSalesAccountingTaxCodes = (taxCodes, accountsIntegration) => {
  return filterToSalesAccountingTaxCodes(taxCodes, accountsIntegration)
      .map(getAccountingTaxCodeOption)
}

export const getPurchaseAccountingTaxCodes = (taxCodes, accountsIntegration) => {
  return filterToPurchaseAccountingTaxCodes(taxCodes, accountsIntegration)
      .map(getAccountingTaxCodeOption)
}

export const filterToSalesAccountingTaxCodes = (taxCodes, accountsIntegration) => {
  switch (accountsIntegration.id) {
    case accountingTypes.XERO.id:
      return taxCodes.filter(code => code.revenue)

    case accountingTypes.QUICK_BOOKS_ONLINE.id:
      return taxCodes.filter(code => code.revenue)

    default:
      return taxCodes
  }
}

export const filterToPurchaseAccountingTaxCodes = (taxCodes, accountsIntegration) => {
  switch (accountsIntegration.id) {
    case accountingTypes.XERO.id:
      return taxCodes.filter(code => !code.revenue)

    case accountingTypes.QUICK_BOOKS_ONLINE.id:
      return taxCodes.filter(code => !code.revenue)

    default:
      return taxCodes
  }
}

export const taxRateIsMismatched = (taxCode, accountingTaxCodes) => {
  const {accountingSalesCode, accountingPurchaseCode} = taxCode

  return (
      taxRateIsMismatchedForAccountingTaxCode(taxCode, accountingSalesCode, accountingTaxCodes) ||
      taxRateIsMismatchedForAccountingTaxCode(taxCode, accountingPurchaseCode, accountingTaxCodes)
  )
}

export const taxRateIsMismatchedForAccountingTaxCode = (taxCode, accountingTaxCode, accountingTaxCodes) => {

  if (isNil(accountingTaxCode))
      return false

  const accountingTaxCodeObject = findAccountingTaxCode(accountingTaxCodes, accountingTaxCode)

  const taxRate = taxCode.rate * 100
  return !isNil(accountingTaxCodeObject) && accountingTaxCodeObject.rate !== taxRate
}

export const getTaxRateDisplay = (taxRate) => {
  const taxPercentage = taxRate * 100
  return Number(formatRoundedNumber(taxPercentage, 3)).toLocaleString(
    undefined,
    { minimumFractionDigits: 0, maximumFractionDigits: 3 })
}
